import {
  Box,
  HStack,
  Image,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {HaliRenderOne} from '@synako/haligator';
import {usePeutVoirDlcStock} from '@/hooks/auth/usePermissionsCommerciales';
import {useApi, useClient} from '@/haligator-factory';
import {isItemStateCatalogueFF, useItemCatalogueOuMarketplace} from 'ff-common';
import {MargesProduit} from '@/domains/ligneProduit/MargesProduit';
import NiceModal from '@ebay/nice-modal-react';
import {ApercuProduitModale} from '@/components/Modale/ApercuProduitModale';
import {DisplayLabels} from '@/domains/panier/components/DisplayLabels';
import {HitItemCatalogue} from 'api-types/ffconnect';
import {LabelDisponibilite} from '@/domains/catalogue/components/LabelDisponibilite';
import {Location} from 'react-router-dom';
import {ListeDrapeaux} from '@/domains/panier/components/listeDrapeaux';
import {useContext} from 'react';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';
import {BreakPointContext} from '@/chakra/BreakpointProvider';
import ColdIcon from '@/components/svg/ColdIcon';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {State} from '@synako/halitrek';
import React from 'react';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useTranslation} from 'react-i18next';
import {PrixTag} from '../PrixTag';
import {ArticleCodesDisplay} from '@/domains/panier/components/ArticleCodesDisplay';
import {ProduitPrixBlock} from '@/domains/produit/ProduitPrixBlock';
import {prixAfficheEstPromo} from '@/domains/panier/PrixEditor/prix-affiche-est-promo';
import {MarketplaceBuyButton} from '@/domains/produit/MarketplaceBuyButton';
import {useBuyingBlocks} from '@/hooks/panier/useBuyingBlocks';
import {useAjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import {useMarketplaceLigneDePanier} from '@/hooks/panier/useMarketplaceLigneDePanier';
import {usePrixCategorieTag} from '@/hooks/usePrixCategorieTag';
import {BoutonDLCStock} from '@/domains/ligneProduit/BoutonDLCStock';
import {TagCadencier} from '@/domains/cadencier/components/TagCadencier';
import {useEditionPrixState} from '@/hooks/panier/usePriceEditor';

interface CatalogProductDetailsProps {
  hit: HitItemCatalogue;
  location: Location;
  ligneDePanier: State<LigneDePanierDesc> | undefined;
}

export const ItemSkeletonMobile: React.FC = () => (
  <>
    <HStack
      justifyContent='space-between'
      width='100%'
    >
      <Box
        mt='5px'
        ml='auto'
        maxW='400px'
        minW='164px'
        flex='1'
      >
        <Skeleton
          height='40px'
          width='100%'
        />
      </Box>
      <VStack
        height='40px'
        width='100%'
        alignItems='flex-end'
        gap='0'
        mx='5'
        flex='2'
      >
        <HStack
          gap='0.5'
          alignItems='start'
        >
          <Skeleton
            height='24px'
            width='80px'
          />
          <Skeleton
            height='16px'
            width='20px'
          />
        </HStack>
        <Skeleton
          height='11px'
          width='60px'
        />
      </VStack>
    </HStack>
    <HStack
      height='94px'
      width='100%'
      flexGrow={1}
    >
      <Skeleton
        height='40px'
        width='100%'
      />
    </HStack>
  </>
);

const ItemSkeletonDesktop: React.FC = () => (
  <>
    <HStack
      justifyContent='end'
      width='100%'
      mt='auto'
    >
      <VStack
        height='40px'
        alignItems='flex-end'
        gap='0'
        mt='10px'
      >
        <HStack
          gap='0.5'
          alignItems='start'
        >
          <Skeleton
            height='24px'
            width='80px'
          />
          <Skeleton
            height='16px'
            width='20px'
          />
          <Skeleton
            height='16px'
            width='60px'
          />
        </HStack>
        <Skeleton
          height='11px'
          width='100px'
        />
      </VStack>
    </HStack>
    <Box
      mt='15px'
      width='100%'
    >
      <Skeleton
        height='40px'
        width='100%'
      />
    </Box>
    <HStack
      height='94px'
      width='100%'
      flexGrow={1}
    >
      <Skeleton
        height='40px'
        width='100%'
      />
    </HStack>
  </>
);

export const CatalogProductDetails: React.FC<CatalogProductDetailsProps> =
  React.memo(({hit, location, ligneDePanier}) => {
    const code_article = hit.code;
    const {t} = useTranslation();
    const articleNav = useApi().follow('get-article', {code_article});
    const clientNav = useClient();
    const {state: itemState, loading: itemLoading} =
      useItemCatalogueOuMarketplace(clientNav, hit);
    const isMobile = useContext(BreakPointContext)?.isMobile;

    const isItemCatalogueFF = isItemStateCatalogueFF(itemState);

    const marketplaceLigneDePanier = useMarketplaceLigneDePanier(
      isItemCatalogueFF ? undefined : hit.marketplace_id,
      hit.code,
    );

    const effectiveLigneDePanier =
      hit.marketplace_id !== 'FF' ? marketplaceLigneDePanier : ligneDePanier;

    // Spécificités propre au commercial
    const peutVoirDlcStock = usePeutVoirDlcStock();

    // Hors panier : la catégorie vient du catalogue, pas de la ligne. Celle-ci
    // n'est passée que pour une info : un prix saisi ou bloqué par le commercial,
    // qui prend alors la place du tag de catégorie.
    const {categorieDeTarif, modeTarifaireSouhaite} = usePrixCategorieTag(
      effectiveLigneDePanier,
      isItemCatalogueFF
        ? itemState.data.tolede.prix_par_livraison?.[0]?.categorie
        : undefined,
      {contexte: 'catalogue'},
    );
    const {prixDraft, onEditionPrixChange} = useEditionPrixState();

    // Nav vers l'item catalogue pour le bouton DLC/Stock
    const itemNav = clientNav.follow('go:item_catalogue_2', {
      marketplace_id: hit.marketplace_id,
      code: hit.code,
    });
    const itemUniteDeCommande = isItemCatalogueFF
      ? itemState.data.unite_de_commande_client
      : undefined;

    const precommande = isItemCatalogueFF
      ? itemState.data.tolede.precommande
      : undefined;

    const itemVignette = imageOptimisee(hit.vignette_url, 200);

    const pays = hit.pays_d_origine ?? [];

    // Logique de l'affichage des disponibilités (voir pour V2)
    // Produit A pour B, on affiche rien => verification de disponible_au_plus_tot si la date correspond au lendemain ou si la date est passée et que precommande est false
    // Produits A pour C : on affiche "à partir du XX/XX/XXXX => affichage de disponible_au_plus_tot si la date ne correspond pas au lendemain, si la date n'est pas passé et si et precommand false
    // Produits en précommande : "Précommander à partir du XX/XX/XXXX => si precommande est true affichage de prochaine_date_livraison_au_plus_tot et si la date n'est pas passé
    const textToast = useSimpleTextToast();

    // Déterminer le contexte en fonction de l'url
    const context = location.pathname.includes('/recherche')
      ? 'search_results'
      : 'catalogue';

    const {uniteQuantiteBlock, buyBlock, commandeEnColis} = useBuyingBlocks(
      textToast,
      context,
      isItemCatalogueFF ? itemState : undefined,
      isItemCatalogueFF ? effectiveLigneDePanier : undefined,
    );
    const {
      ajouterAvecPrixSaisi,
      isAddingAvecPrix,
      saisieAvantAjout,
      codeArticle,
    } = useAjouterAvecPrixSaisi(
      isItemCatalogueFF ? itemState : undefined,
      commandeEnColis,
    );
    // Le tag est déjà rendu à côté du prix : on ne le repasse pas au bloc,
    // sinon il s'affiche en double. Le verrou marché se fait ici.
    const prixMarche =
      hit.nature_du_prix === 'marche' ||
      categorieDeTarif === 'M' ||
      categorieDeTarif === 'N' ||
      categorieDeTarif === 'S';

    return (
      <>
        {!isMobile ? (
          /* Version desktop */
          <VStack
            minH='470px'
            maxH={peutVoirDlcStock && isItemCatalogueFF ? '530px' : '490px'}
            minW='240px'
            maxW='380px'
            padding='20px'
            border='1px'
            borderColor='gray.light'
            position='relative'
          >
            {/* Affichages Badges « Cadencier » et/ou « Produit marché » */}
            <VStack
              position='absolute'
              top={precommande ? '45px' : '10px'}
              left='10px'
              align='start'
              spacing='1'
              transition='top 0.3s ease-in-out'
            >
              {/* Affichage du tag « Cadencier » */}
              <TagCadencier code_article={hit.code} />
              {/* Affichage de "Produit marché" */}
              {hit.produit_marche && (
                <HaliRenderOne
                  nav={clientNav}
                  render={({data: clientData}) => {
                    if (clientData.affichage_produit_marche === 'active') {
                      return (
                        <HStack
                          bg='blue.main_light'
                          color='blue.main'
                          fontSize='xs'
                          fontWeight='bold'
                          py='2px'
                          px='8px'
                          borderRadius='md'
                        >
                          <Text>{t('global.produit_marche')}</Text>
                        </HStack>
                      );
                    }
                    return null;
                  }}
                />
              )}
            </VStack>
            <>
              <LabelDisponibilite precommande={precommande} />
              <Box
                cursor='pointer'
                onClick={() => {
                  NiceModal.show(ApercuProduitModale, {
                    hit,
                    location,
                    imageUrl: itemVignette,
                  });
                }}
                w='100%'
              >
                <Stack
                  justifyContent='center'
                  alignItems='center'
                  width='100%'
                  height='134px'
                  flexGrow={1}
                  mb='10px'
                >
                  <Box
                    width='100%'
                    height='134px'
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                  >
                    <Image
                      src={itemVignette}
                      alt={hit.libelle_commercial}
                      objectFit='contain'
                      maxWidth='100%'
                      maxHeight='100%'
                      width='auto'
                      height='auto'
                    />
                  </Box>
                </Stack>
                <Box height='35px'>
                  {hit.temperature_livraison === 'negatif' && (
                    <ColdIcon
                      boxSize='25px'
                      mb='20px'
                    />
                  )}
                </Box>
                <VStack
                  height='115px'
                  width='100%'
                  flexGrow={1}
                  alignItems='rigth'
                >
                  <HStack
                    height='22px'
                    alignItems='center'
                  >
                    <ArticleCodesDisplay
                      code_article={code_article}
                      marketplace_id={hit.marketplace_id}
                      toledeData={
                        isItemStateCatalogueFF(itemState)
                          ? itemState.data.tolede
                          : undefined
                      }
                    />
                    <MargesProduit
                      code_article={code_article}
                      ligneDePanier={effectiveLigneDePanier}
                      isItemCatalogueFF={isItemCatalogueFF}
                      fontSize='10px'
                      fontWeight='600'
                      prixDraft={prixDraft}
                    />
                  </HStack>
                  <VStack
                    minHeight='70px'
                    alignItems='left'
                  >
                    <Text
                      align='left'
                      color='black'
                      fontSize='sm'
                      lineHeight='18px'
                      fontWeight='600'
                      noOfLines={peutVoirDlcStock ? 2 : 3}
                    >
                      {hit.libelle_commercial}
                    </Text>
                    <HStack>
                      {(hit.marque ?? '').length > 1 && (
                        <Text
                          align='left'
                          color='black'
                          fontSize='xs'
                          lineHeight='18px'
                        >
                          {hit.marque}
                        </Text>
                      )}
                      {isItemCatalogueFF && (
                        <HaliRenderOne
                          nav={articleNav}
                          skeleton={() => null}
                          render={({state: articlestate}) => {
                            return (
                              <DisplayLabels articleState={articlestate} />
                            );
                          }}
                        />
                      )}
                      <ListeDrapeaux
                        listepays={pays}
                        max={5}
                      />
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            </>
            {itemLoading || !itemState ? (
              <ItemSkeletonDesktop />
            ) : (
              <>
                {peutVoirDlcStock && isItemCatalogueFF && (
                  <HStack
                    width='100%'
                    justifyContent='flex-start'
                    alignItems='start'
                    minHeight='40px'
                  >
                    <BoutonDLCStock
                      itemNav={itemNav}
                      itemUniteDeCommande={itemUniteDeCommande}
                    />
                  </HStack>
                )}
                {isItemCatalogueFF ? (
                  <HStack
                    width='100%'
                    alignItems='flex-start'
                    mt='auto'
                  >
                    <PrixTag
                      typePrix={hit.nature_du_prix}
                      categorieDeTarif={categorieDeTarif}
                      modeTarifaireSouhaite={modeTarifaireSouhaite}
                    />
                    <VStack
                      alignItems='flex-end'
                      gap='0'
                      ml='auto'
                    >
                      <ProduitPrixBlock
                        item={itemState}
                        estPromo={prixAfficheEstPromo(
                          hit.nature_du_prix,
                          categorieDeTarif,
                        )}
                        ligneDePanier={effectiveLigneDePanier}
                        saisieAvantAjout={saisieAvantAjout && !prixMarche}
                        ajouterAvecPrixSaisi={ajouterAvecPrixSaisi}
                        codeArticle={codeArticle}
                        onEditionPrixChange={onEditionPrixChange}
                      />
                    </VStack>
                  </HStack>
                ) : (
                  <HStack
                    justifyContent='end'
                    width='100%'
                    mt='auto'
                  >
                    <VStack
                      alignItems='flex-end'
                      gap='0'
                      mt='10px'
                    >
                      <ProduitPrixBlock item={itemState} />
                    </VStack>
                  </HStack>
                )}
                <Box
                  pt='10px'
                  width='100%'
                >
                  {uniteQuantiteBlock()}
                </Box>
                <HStack
                  height='94px'
                  width='100%'
                  flexGrow={1}
                  alignItems='end'
                  mt='auto'
                  position='relative'
                >
                  {hit.marketplace_id !== 'FF' ? (
                    <MarketplaceBuyButton
                      itemState={itemState}
                      ligneDePanier={ligneDePanier}
                      marketplaceLigneDePanier={marketplaceLigneDePanier}
                      context={context}
                      minW='100%'
                      mt='0'
                    />
                  ) : (
                    buyBlock({
                      afficher_bouton_liste: true,
                      minW: '100%',
                      mt: '0',
                      somethingSubmitting: isAddingAvecPrix,
                    })
                  )}
                </HStack>
              </>
            )}
          </VStack>
        ) : (
          /* Version mobile */
          <VStack
            height={peutVoirDlcStock && isItemCatalogueFF ? '290px' : '255px'}
            width='100%'
            padding='20px'
            pt='30px'
            borderBottom='1px'
            borderColor='gray.light'
            position='relative'
          >
            <HStack
              position='absolute'
              left='2px'
              top='0'
            >
              <LabelDisponibilite precommande={precommande} />
            </HStack>
            <Box
              cursor='pointer'
              onClick={() => {
                NiceModal.show(ApercuProduitModale, {
                  hit,
                  location,
                  imageUrl: itemVignette,
                });
              }}
              w='100%'
            >
              <HStack
                height='85px'
                width='100%'
                mb='10px'
                alignItems='flex-start'
                justifyContent='flex-start'
                spacing={4}
              >
                <Box
                  boxSize='90px'
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  position='relative'
                >
                  <Image
                    src={itemVignette}
                    alt={hit.libelle_commercial}
                    objectFit='contain'
                    maxWidth='100%'
                    maxHeight='100%'
                    width='auto'
                    height='auto'
                  />
                  <Box
                    position='absolute'
                    bottom='0'
                    left='0'
                  >
                    {hit.temperature_livraison === 'negatif' && (
                      <ColdIcon boxSize='25px' />
                    )}
                  </Box>
                </Box>
                <VStack
                  alignItems='flex-start'
                  spacing={1}
                >
                  <HStack>
                    <ArticleCodesDisplay
                      code_article={code_article}
                      marketplace_id={hit.marketplace_id}
                      toledeData={
                        isItemStateCatalogueFF(itemState)
                          ? itemState.data.tolede
                          : undefined
                      }
                    />
                    <MargesProduit
                      code_article={code_article}
                      ligneDePanier={effectiveLigneDePanier}
                      isItemCatalogueFF={isItemCatalogueFF}
                      prixDraft={prixDraft}
                    />
                    {/* Affichage du tag « Cadencier » */}
                    <TagCadencier code_article={hit.code} />
                    {/* Affichage de "Produit marché" */}
                    {hit.produit_marche && (
                      <HaliRenderOne
                        nav={clientNav}
                        render={({data: clientData}) => {
                          if (
                            clientData.affichage_produit_marche === 'active'
                          ) {
                            return (
                              <HStack
                                color='blue.main'
                                fontSize='xs'
                                fontWeight='bold'
                                py='2px'
                                px='8px'
                              >
                                <Text>{t('global.produit_marche')}</Text>
                              </HStack>
                            );
                          }
                          return null;
                        }}
                      />
                    )}
                  </HStack>
                  <VStack
                    spacing={0}
                    alignItems='start'
                  >
                    <Text
                      align='left'
                      color='black'
                      fontSize='sm'
                      lineHeight='18px'
                      fontWeight='600'
                      noOfLines={3}
                    >
                      {hit.libelle_commercial}
                    </Text>
                    <HStack mt={1}>
                      {(hit.marque ?? '').length > 1 && (
                        <Text
                          align='left'
                          color='black'
                          fontSize='xs'
                          lineHeight='18px'
                        >
                          {hit.marque}
                        </Text>
                      )}
                      {isItemCatalogueFF && (
                        <HaliRenderOne
                          nav={articleNav}
                          skeleton={() => null}
                          render={({state: articlestate}) => {
                            return (
                              <DisplayLabels articleState={articlestate} />
                            );
                          }}
                        />
                      )}
                      <ListeDrapeaux
                        listepays={pays}
                        max={9}
                      />
                    </HStack>
                  </VStack>
                </VStack>
              </HStack>
            </Box>
            {itemLoading || !itemState ? (
              <ItemSkeletonMobile />
            ) : (
              <>
                {peutVoirDlcStock && isItemCatalogueFF && (
                  <HStack
                    width='100%'
                    justifyContent='flex-start'
                    minH='20px'
                  >
                    <BoutonDLCStock
                      itemNav={itemNav}
                      itemUniteDeCommande={itemUniteDeCommande}
                    />
                  </HStack>
                )}
                <HStack
                  justifyContent='space-between'
                  width='100%'
                >
                  <Box
                    mt='5px'
                    ml='auto'
                    maxW='400px'
                    minW='164px'
                    flex='1'
                  >
                    {uniteQuantiteBlock()}
                  </Box>
                  <VStack
                    width='100%'
                    alignItems='flex-end'
                    gap='0'
                    mx='5'
                    flex='2'
                  >
                    <PrixTag
                      typePrix={hit.nature_du_prix}
                      categorieDeTarif={categorieDeTarif}
                      modeTarifaireSouhaite={modeTarifaireSouhaite}
                    />
                    <ProduitPrixBlock
                      item={itemState}
                      estPromo={prixAfficheEstPromo(
                        hit.nature_du_prix,
                        categorieDeTarif,
                      )}
                      ligneDePanier={effectiveLigneDePanier}
                      saisieAvantAjout={saisieAvantAjout && !prixMarche}
                      ajouterAvecPrixSaisi={ajouterAvecPrixSaisi}
                      codeArticle={codeArticle}
                      onEditionPrixChange={onEditionPrixChange}
                    />
                  </VStack>
                </HStack>
                <HStack
                  height='94px'
                  width='100%'
                  flexGrow={1}
                  position='relative'
                >
                  {hit.marketplace_id !== 'FF' ? (
                    <MarketplaceBuyButton
                      itemState={itemState}
                      ligneDePanier={ligneDePanier}
                      marketplaceLigneDePanier={marketplaceLigneDePanier}
                      context={context}
                      minW='100%'
                      mt='0'
                    />
                  ) : (
                    buyBlock({
                      afficher_bouton_liste: true,
                      minW: '100%',
                      mt: '0',
                      somethingSubmitting: isAddingAvecPrix,
                    })
                  )}
                </HStack>
              </>
            )}
          </VStack>
        )}
      </>
    );
  });
